/** Is this app running with NO SERVER behind it? — phase 4's prerequisite.
 *
 * ⚠⚠ **THE DESKTOP APP HAS NOWHERE TO LOG IN TO, AND THE GATE WOULD STOP IT DEAD.** `App.tsx`
 * asks `whoAmI()` before it renders anything; with no server that answers nothing and the
 * drafter lands on a login screen they cannot pass. Phase 3 already made the drawing itself
 * server-free — the reader, the import, the display context, the picture and now the DXF writer
 * are all `pdcad-core` — so what is left is to let the app START that way.
 *
 * ⚠⚠ **DETECTED, NEVER GUESSED.** A network failure is NOT standalone: the web app talking to a
 * server that is down must say so rather than quietly becoming a different product with no
 * autosave to the database and no drawing list. So this is true only when the app is genuinely
 * running as one — inside Tauri, or asked for explicitly.
 *
 * ⚠ `?standalone=1` is the same mode in an ordinary browser. It is how the desktop app can be
 * tried and measured before there is a desktop app, and it is what `qa/standalone_shot.py`
 * drives. Not a debug flag to be removed later: a drafter with no network is the same case.
 */

/** Tauri injects these on the window before any script runs. ⚠ Both spellings: v1 published
 *  `__TAURI__`, v2 `__TAURI_INTERNALS__`, and a shell that sets neither is not Tauri. */
function inTauri(): boolean {
  if (typeof window === "undefined") return false;
  const w = window as unknown as Record<string, unknown>;
  return w.__TAURI_INTERNALS__ !== undefined || w.__TAURI__ !== undefined;
}

let asked: boolean | null = null;

/** Does this app run without a server? ⚠ Read ONCE: the answer cannot change while the window
 *  is open, and a value that could would make every caller ask when it mattered. */
export function standalone(): boolean {
  if (asked === null) {
    let flag = false;
    try {
      flag = typeof window !== "undefined"
        && new URLSearchParams(window.location.search).get("standalone") === "1";
    } catch {
      flag = false;
    }
    asked = inTauri() || flag;
  }
  return asked;
}

/** Who the app says you are when there is nobody to ask.
 *
 * ⚠ NOT a pretend login. Nothing is authorised by it — in standalone there is no server to
 * authorise anything with — and it exists because the studio takes a `Who` to put a name in the
 * corner. Calling it `you` rather than inventing a username says exactly that.
 */
export const OFFLINE_WHO = { id: 0, username: "you", name: "this PC", staff: false };
